<template>
  <div :class="template">
    <b-overlay
      :show="!permission"
      rounded="lg"
      spinner-variant="customer"
      id="app"
      :style="cssProps"
    >
      <template v-if="$auth.check()">
        <!-- Modern Layout (Sidebar + TopBar) -->
        <div v-if="layoutMode === 'modern'" class="app-layout">
          <!-- Sidebar Navigation -->
          <SidebarNav :class="{ 'sidebar-open': sidebarOpen }" />

          <!-- Mobile Backdrop -->
          <div
            v-if="sidebarOpen"
            class="sidebar-backdrop"
            @click="sidebarOpen = false"
          ></div>

          <!-- Main Content Area -->
          <div class="main-area">
            <!-- Top Bar -->
            <TopBar
              @toggle-sidebar="sidebarOpen = !sidebarOpen"
              :layout-mode="layoutMode"
              :property-info="selectedPropertyInfo"
              @change-layout="setLayoutMode"
              @go-back="clearPropertySelection"
            />

            <!-- Page Content -->
            <main class="page-content">
              <router-view :layout-mode="layoutMode" />
            </main>
          </div>
        </div>

        <!-- Classic Layout (NavBar + Header + Footer) -->
        <div v-else class="bg-global main-app-wrapper">
          <NavBar :layout-mode="layoutMode" @change-layout="setLayoutMode" />

          <!-- Property Indicator Bar -->
          <transition name="slide-down">
            <div v-if="selectedPropertyInfo" class="property-indicator-bar">
              <div class="property-indicator-content">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 9.5L12 4L21 9.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M19 13V19.4C19 19.7314 18.7314 20 18.4 20H5.6C5.26863 20 5 19.7314 5 19.4V13"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span class="property-indicator-text">
                  <strong>{{ selectedPropertyInfo.title }}</strong>
                  <span class="property-indicator-separator">•</span>
                  {{ selectedPropertyInfo.project }}
                </span>
              </div>
            </div>
          </transition>

          <Header />
          <div class="classic-content-area">
            <div
              :class="[
                'content-wrapper',
                {
                  'full-width':
                    !rightSidebarData || rightSidebarData.length === 0,
                },
              ]"
            >
              <router-view :layout-mode="layoutMode" />
            </div>
            <transition name="sidebar-slide">
              <div
                class="right-sidebar-wrapper"
                v-if="rightSidebarData && rightSidebarData.length > 0"
              >
                <Sidebar :sidebar-data="rightSidebarData" />
              </div>
            </transition>
          </div>
          <Footer />
        </div>
      </template>
      <router-view v-else></router-view>
    </b-overlay>
  </div>
</template>

<script>
import SidebarNav from "./views/private/components/SidebarNav.vue";
import TopBar from "./views/private/components/TopBar.vue";
import NavBar from "./views/private/components/NavBar.vue";
import Header from "./views/private/components/Header.vue";
import Footer from "./views/private/components/Footer.vue";
import Sidebar from "./views/private/views/sidebar/Sidebar.vue";
export default {
  name: "App",
  data() {
    return {
      user: null,
      asset_id: null,
      apartments: [],
      tab_index: 0,
      selectedApartment: null,
      sidebarOpen: false,
      layoutMode: "classic", // 'modern' or 'classic'
      rightSidebarData: [],
      selectedPropertyInfo: null,
    };
  },
  components: { SidebarNav, TopBar, NavBar, Header, Footer, Sidebar },
  mounted() {
    try {
      localStorage.setItem("layoutMode", "classic");
    } catch (e) {
      // ignore
    }

    // Listen for property selection from $root (for router-view compatibility)
    this.$root.$on("property-selected", this.updateSelectedProperty);
    // Listen for sidebar data changes from $root
    this.$root.$on("sidebar-data-changed", this.updateSidebarData);
  },
  beforeDestroy() {
    // Clean up event listeners
    this.$root.$off("property-selected", this.updateSelectedProperty);
    this.$root.$off("sidebar-data-changed", this.updateSidebarData);
  },
  methods: {
    setLayoutMode() {
      this.layoutMode = "classic";
      try {
        localStorage.setItem("layoutMode", "classic");
      } catch (e) {
        // ignore
      }
    },
    updateSidebarData(data) {
      this.rightSidebarData = data || [];
    },
    updateSelectedProperty(propertyInfo) {
      this.selectedPropertyInfo = propertyInfo;
    },
    clearPropertySelection() {
      this.selectedPropertyInfo = null;
      // Emit event to child to go back to grid
      this.$root.$emit("go-back-to-grid");
    },
    logout() {
      this.$auth.logout({
        redirect: `/app/${this.$route.params.customer}/login`,
        success: function (msg) {
          console.error("LOGIN:", msg);
        },
      });
    },
  },
  watch: {
    "$route.name": {
      handler: function () {
        // El sidebar derecho y la barra de propiedad solo aplican al detalle del bien.
        // Si navegamos a otra ruta, limpiamos el estado para que no quede persistente.
        if (this.$route.name !== "asset-detail") {
          this.rightSidebarData = [];
          this.selectedPropertyInfo = null;
        }
      },
      immediate: true,
    },
    "$route.params.customer": {
      handler: function () {
        this.$store.commit(
          "setCustomer",
          this.$route.params.customer == "rimaza"
            ? "activa"
            : this.$route.params.customer
        );
        this.$store.commit("setCustomerData");
        if (this.$auth.check()) this.user = this.$auth.user();
      },
      immediate: true,
    },
    customerId: {
      handler: function () {
        if (this.customerId && this.user) {
          if (!(this.user.customer_id == this.customerId)) {
            this.logout();
          }
        }
      },
      immediate: true,
    },
  },
  computed: {
    cssProps() {
      return {
        "--color": `linear-gradient(90deg, ${this.color}, ${this.colorB})`,
        "--color-a": this.color,
        "--color-background": `linear-gradient(90deg, ${this.colorBackground}, ${this.colorBackgroundB})`,
        "--color-background-a": this.colorBackground,
        "--color-btn": `linear-gradient(90deg, ${this.colorBtn}, ${this.colorBtnB})`,
        "--color-btn-a": this.colorBtn,
        "--color-btn-text": this.colorBtnText,
        "--color-menu": `linear-gradient(90deg, ${this.colorMenu}, ${this.colorMenuB})`,
        "--color-menu-a": this.colorMenu,
        "--color-menu-text": this.colorMenuText,
        "--color-footer": `linear-gradient(90deg, ${this.colorFooter}, ${this.colorFooterB})`,
        "--color-footer-a": this.colorFooter,
        "--color-footer-text": this.colorFooterText,
        "--color-header": `linear-gradient(90deg, ${this.colorHeader}, ${this.colorHeaderB})`,
        "--color-header-a": this.colorHeader,
        "--color-text-header": this.colorTextHeader,
        "--color-var": `linear-gradient(90deg, ${this.colorVar}, ${this.colorVarB})`,
        "--color-var-text": this.colorVarText,
        "--color-var-a": this.colorVar,
        "--color-text": this.colorText,
        "--color-login-btn": `linear-gradient(90deg, ${this.colorLoginBtn}, ${this.colorLoginBtnB})`,
        "--color-login-btn-a": this.colorLoginBtn,
        "--color-login-btn-text": this.colorLoginBtnText,
      };
    },
    template() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.template
        : "default";
    },
    color() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color
        : null;
    },
    colorBackground() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_background
        : null;
    },
    colorBtn() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_btn
        : null;
    },
    colorBtnText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_btn_text
        : null;
    },
    colorFooter() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_footer
        : null;
    },
    colorFooterText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_footer_text
        : null;
    },
    colorLoginBtn() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_login_btn
        : null;
    },
    colorLoginBtnText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_login_btn_text
        : null;
    },
    colorMenu() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_menu
        : null;
    },
    colorMenuText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_menu_text
        : null;
    },
    colorText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_text
        : null;
    },
    colorHeader() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_header
        : null;
    },
    colorTextHeader() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_text_header
        : null;
    },
    colorVar() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_var
        : null;
    },
    colorB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_b
        : null;
    },
    colorBackgroundB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_background_b
        : null;
    },
    colorBtnB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_btn_b
        : null;
    },
    colorFooterB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_footer_b
        : null;
    },
    colorLoginBtnB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_login_btn_b
        : null;
    },
    colorMenuB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_menu_b
        : null;
    },
    colorHeaderB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_header_b
        : null;
    },
    colorVarB() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_var_b
        : null;
    },
    colorVarText() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.color_var_text
        : null;
    },

    permission() {
      if (this.$store.getters.getCustomerData) {
        return this.$store.getters.getCustomerData.products.find(
          (product) => product.name == "Sala de Ventas Virtual"
        )
          ? true
          : false;
      } else {
        return false;
      }
    },
    customerId() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.customer_id
        : null;
    },
  },
};
</script>

<style>
/* ===== Modern App Layout ===== */
.app-layout {
  display: flex;
  min-height: 100vh;
  background-color: var(--color-background-a, #f4f6f8);
  background: var(--color-background, var(--color-background-a, #f4f6f8));
}

.main-area {
  flex: 1;
  margin-left: 260px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  transition: margin-left 0.3s ease;
}

.page-content {
  flex: 1;
  padding: 24px;
}

/* ===== Primary Color (Backend) for Main Content =====
   Normaliza el color "primary" dentro del contenido que cambia por módulo.
   Fuente: --color-a (customerData.color)
*/
.page-content,
.content-wrapper {
  --primary-color: var(--color-a, #3b82f6);
  --primary-gradient: var(
    --color,
    linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)
  );
  --theme-accent: var(--primary-color);
  accent-color: var(--primary-color);

  /* Dentro del contenido, forzar que los estilos que consumen --color-btn* usen el primary */
  --color-btn: var(--primary-color);
  --color-btn-a: var(--primary-color);

  /* Muchos módulos usan --color-btn/--color-btn-a (no .text-primary).
     Para que el "primary" se vea en el contenido, lo mapeamos al primary del backend. */
  --color-btn: var(--primary-color);
  --color-btn-a: var(--primary-color);
  --theme-primary: var(--primary-color);
  --theme-primary-hover: var(--primary-color);
}

.page-content a,
.content-wrapper a,
.page-content .btn-link,
.content-wrapper .btn-link {
  color: var(--primary-color);
}

.page-content .text-primary,
.content-wrapper .text-primary {
  color: var(--primary-color) !important;
}

.page-content .bg-primary,
.content-wrapper .bg-primary {
  background-color: var(--primary-color) !important;
}

.page-content .border-primary,
.content-wrapper .border-primary {
  border-color: var(--primary-color) !important;
}

.page-content .page-link,
.content-wrapper .page-link {
  color: var(--primary-color);
}

.page-content .page-item.active .page-link,
.content-wrapper .page-item.active .page-link {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

/* Botones Bootstrap dentro del contenido: usar primary del backend */
.page-content .btn-primary,
.content-wrapper .btn-primary,
.page-content .btn-customer,
.content-wrapper .btn-customer {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: var(--color-btn-text, #ffffff) !important;
  background-image: none !important;
}

.page-content .btn-outline-primary,
.content-wrapper .btn-outline-primary {
  color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
}

.page-content .btn-outline-primary:hover,
.content-wrapper .btn-outline-primary:hover {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: var(--color-btn-text, #ffffff) !important;
}

/* Sidebar Backdrop for Mobile */
.sidebar-backdrop {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
}

/* ===== Classic App Layout ===== */
.main-app-wrapper {
  background-color: var(--color-background-a, #f4f6f8);
  background: var(--color-background, var(--color-background-a, #f4f6f8));
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  /* Layout clásico: variables compartidas para alinear navbar/header/contenido/footer */
  --classic-container-max: 1400px;
  --classic-container-width: 100%;
  --classic-container-pad-x: 24px;
}

.classic-content-area {
  display: flex;
  gap: 24px;
  width: var(--classic-container-width);
  max-width: var(--classic-container-max);
  margin-left: auto;
  margin-right: auto;
  padding: 24px var(--classic-container-pad-x);
  box-sizing: border-box;
  align-items: flex-start;
}

@media (min-width: 1025px) {
  .main-app-wrapper {
    --classic-container-width: min(88vw, var(--classic-container-max));
  }
}

.content-wrapper {
  flex: 0 0 70%;
  max-width: 70%;
  min-width: 0;
  padding: 35px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: flex 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.content-wrapper.full-width {
  flex: 0 0 100%;
  max-width: 100%;
}

.right-sidebar-wrapper {
  flex: 0 0 calc(30% - 24px); /* 30% minus the gap */
  max-width: calc(30% - 24px);
  position: sticky;
  top: 120px; /* navbar (70px) + property indicator bar (~40px) + spacing */
  align-self: flex-start;
  max-height: calc(100vh - 140px);
  overflow-y: auto;

  /* Sidebar (derecho) con color principal */
  --primary-color: var(--color-a, #3b82f6);
  --color-btn: var(--primary-color);
  --color-btn-a: var(--primary-color);
  --theme-primary: var(--primary-color);
  --theme-primary-hover: var(--primary-color);
}

/* Sidebar slide transition */
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-slide-enter,
.sidebar-slide-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.sidebar-slide-enter-to,
.sidebar-slide-leave {
  opacity: 1;
  transform: translateX(0);
}

.right-sidebar-wrapper:empty {
  display: none;
}

/* Responsive Adjustments */
@media (max-width: 1024px) {
  .main-area {
    margin-left: 72px;
  }

  .main-app-wrapper {
    --classic-container-pad-x: 16px;
  }

  .classic-content-area {
    flex-direction: column;
    padding: 16px var(--classic-container-pad-x);
    gap: 0;
  }

  .content-wrapper {
    flex: 0 0 100%;
    max-width: 100%;
    width: 100%;
  }

  .content-wrapper.full-width {
    flex: 0 0 100%;
    max-width: 100%;
    width: 100%;
  }

  .right-sidebar-wrapper {
    flex: 0 0 100%;
    max-width: 100%;
    width: 100%;
    position: static;
    margin-top: 16px;
  }
}

@media (max-width: 768px) {
  .classic-content-area {
    padding: 12px var(--classic-container-pad-x);
  }

  .main-app-wrapper {
    --classic-container-pad-x: 12px;
  }

  .content-wrapper {
    padding: 16px;
  }

  .right-sidebar-wrapper {
    margin-top: 12px;
  }
}

@media (max-width: 576px) {
  .content-wrapper {
    border-radius: 20px;
  }
}

@media (max-width: 768px) {
  .main-area {
    margin-left: 0;
  }

  .sidebar-backdrop {
    display: block;
  }

  .page-content {
    padding: 16px;
  }
}

/* Property Indicator Bar */
.property-indicator-bar {
  background: var(--color-a, #3b82f6);
  color: var(--color-btn-text, #ffffff);
  padding: 10px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 70px; /* Height of navbar */
  z-index: 999;
}

.property-indicator-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.property-indicator-content svg {
  flex-shrink: 0;
  opacity: 0.9;
}

.property-indicator-text {
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.property-indicator-text strong {
  font-weight: 600;
  text-transform: capitalize;
}

.property-indicator-separator {
  opacity: 0.6;
}

@media (max-width: 768px) {
  .property-indicator-bar {
    top: 64px; /* Mobile navbar height */
  }
}

@media (max-width: 576px) {
  .property-indicator-bar {
    padding: 8px 16px;
  }

  .property-indicator-text {
    font-size: 0.85rem;
  }
}

/* Property Indicator Bar Animation */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>

<style lang="scss">
@import "assets/sass/main";
.default {
  font-family: "Lato", sans-serif !important;
  @import "assets/sass/templates/default";
}
.cuadrado {
  font-family: "Montserrat", sans-serif !important;
  @import "assets/sass/templates/cuadrado";
}
.redondo {
  font-family: "Lato", sans-serif !important;
  @import "assets/sass/templates/redondo";
}
</style>
